import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CompanyChip from '../../components/asset-company/CompanyChip';
import LicenceForm from '../../components/licences/LicenceForm';
import { Paperclip } from '../../components/ChatFile';
import { downloadCsv, downloadXlsx } from '../../lib/exportTable';
import { useFeature } from '../../lib/features';
import {
  licencesApi, errorOf, TYPE_LABEL, STATUS_LABEL, statusTone, nextWords, noticeChip, seatWords, seatShare, costWords, amount, totals
} from '../../lib/licences';
import '../../components/Assets.css';
import '../../components/licences/Licences.css';

/**
 * THE LICENCES REGISTER (119), Asset Portal -> Licences. Administrators.
 *
 * One header (the title, "Add a licence", the way back to the devices),
 * the summary - what the active licences cost a year, what renews in the
 * next 90 days, the seats, what is over-allocated - each of the last three a
 * filter when pressed; one filter bar (search, status, vendor, company,
 * type, renewing within, over-allocated, sort), all in the address so a
 * filtered list can be bookmarked or sent; the list; and the export, CSV or
 * Excel, of exactly the rows the filters match (GET /licences/export, through
 * lib/exportTable.js so the figures are the screen's). Wave 7: a paperclip
 * beside a licence with documents, and the notice deadline under its next
 * date when it has a notice period.
 */
const FILTERS = ['q', 'status', 'vendor', 'company', 'type', 'due', 'over', 'sort'];

export default function LicencesList() {
  const navigate = useNavigate();
  const assetsOn = useFeature('assets');
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState('');
  const [query, setQuery] = useState(params.get('q') || '');

  const filters = useMemo(() => Object.fromEntries(FILTERS.map((k) => [k, params.get(k) || ''])
    .filter(([, v]) => v !== '')), [params]);
  const key = JSON.stringify(filters);

  // Only the latest request's answer is shown: a slower, older one (the
  // search a moment ago) arriving last would otherwise put its rows back.
  const latest = useRef(0);
  const load = useCallback(() => {
    const f = JSON.parse(key);
    const n = ++latest.current;
    return Promise.all([licencesApi.list(f), licencesApi.summary()])
      .then(([list, sum]) => { if (n !== latest.current) return; setData(list); setSummary(sum); setError(''); })
      .catch((e) => { if (n === latest.current) setError(errorOf(e, 'Could not load the licences.')); });
  }, [key]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { licencesApi.options().then(setOptions).catch(() => setOptions({ people: [], companies: [] })); }, []);

  // The address as it is this moment. The router's own copy (params) is
  // updated in a transition, so a click just made may not be in it yet: a
  // filter switched off just before typing came back when the search wrote
  // the address from it.
  const liveParams = () => new URLSearchParams(window.location.search);

  // The search box writes the address a moment after typing stops.
  useEffect(() => {
    const t = setTimeout(() => {
      const now = liveParams();
      if ((now.get('q') || '') === query.trim()) return;
      if (query.trim()) now.set('q', query.trim()); else now.delete('q');
      setParams(now, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (k, v) => {
    const next = liveParams();
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };
  const toggleParam = (k, v) => setParam(k, liveParams().get(k) === v ? '' : v);
  const anyFilter = FILTERS.some((k) => k !== 'sort' && params.get(k));
  const clear = () => { setQuery(''); setParams(new URLSearchParams()); };

  const rows = data?.licences || [];
  const companies = (options?.companies || []).filter((c) => !c.archived_at);
  const closeForm = useCallback(() => setAdding(false), []);

  const exportAs = async (kind) => {
    setExporting(kind);
    try {
      const r = await licencesApi.exportRows(filters);
      const name = `licences-${r.today || 'list'}`;
      if (kind === 'csv') downloadCsv(r.columns, r.rows, name); else downloadXlsx(r.columns, r.rows, name, 'Licences');
    } catch (e) {
      setError(errorOf(e, 'The export did not work.'));
    }
    setExporting('');
  };

  const renew = summary?.renewing_90;
  const first = renew?.first?.[0];

  return (
    <div className="container" data-testid="licences-page">
      <div className="page-header as-reg-head">
        <div className="as-reg-heading">
          <h1 className="page-title">Licences</h1>
          <p className="page-description">
            Every software licence and subscription the firm pays for: who has a seat, what it costs and when it renews.
          </p>
        </div>
        <div className="as-reg-add">
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>Add a licence</button>
        </div>
        <nav className="as-reg-links" aria-label="Asset Portal">
          <ul>
            {/* Not while the Asset Portal is off (wave 7): Licences is then reached from Advanced. */}
            {assetsOn && <li><Link to="/assets">Devices</Link></li>}
            {assetsOn && <li><Link to="/assets/leavers">Leavers</Link></li>}
            <li><Link to="/people">Joiners &amp; leavers</Link></li>
          </ul>
        </nav>
      </div>

      {summary && (
        <div className="lic-summary" data-testid="licence-summary">
          <div className="lic-stat" data-stat="annual">
            <span className="lic-stat-label">A year</span>
            <span className="lic-stat-value">{totals(summary.annual_cost, { round: true })}</span>
            <span className="lic-stat-sub">{`${summary.active} active licence${summary.active === 1 ? '' : 's'}`}</span>
          </div>
          <button type="button" className="lic-stat" data-stat="renewing" aria-pressed={params.get('due') === '90'}
            data-tone={renew?.count ? 'warning' : undefined} onClick={() => toggleParam('due', '90')}>
            <span className="lic-stat-label">Renewing in 90 days</span>
            <span className="lic-stat-value">{renew?.count || 0}</span>
            <span className="lic-stat-sub">
              {first ? `First: ${first.product}, in ${first.days_left} day${first.days_left === 1 ? '' : 's'}` : 'Nothing due'}
              {renew?.count ? ` · ${totals(renew.annual_cost, { round: true })} a year` : ''}
            </span>
          </button>
          <div className="lic-stat" data-stat="seats">
            <span className="lic-stat-label">Seats in use</span>
            <span className="lic-stat-value">{`${summary.seats.used} of ${summary.seats.purchased}`}</span>
            <span className="lic-stat-sub">
              {summary.seats.unlimited ? `${summary.seats.unlimited} licence${summary.seats.unlimited === 1 ? '' : 's'} with no limit` : 'Across the licences with a limit'}
            </span>
          </div>
          <button type="button" className="lic-stat" data-stat="over" aria-pressed={params.get('over') === 'true'}
            data-tone={summary.over_allocated ? 'critical' : undefined} onClick={() => toggleParam('over', 'true')}>
            <span className="lic-stat-label">Over-allocated</span>
            <span className="lic-stat-value">{summary.over_allocated}</span>
            <span className="lic-stat-sub">{summary.over_allocated ? 'More seats given than bought' : 'Every licence within its seats'}</span>
          </button>
        </div>
      )}

      <div className="as-reg-filters" role="search" aria-label="Filter the licences">
        <div className="as-toolbar">
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Product, vendor, reference or company" aria-label="Search the licences" />
          <select value={params.get('status') || ''} aria-label="Status" onChange={(e) => setParam('status', e.target.value)}>
            <option value="">{`Active${summary ? ` (${summary.by_status.active})` : ''}`}</option>
            <option value="cancelled">{`Cancelled${summary ? ` (${summary.by_status.cancelled})` : ''}`}</option>
            <option value="expired">{`Expired${summary ? ` (${summary.by_status.expired})` : ''}`}</option>
            <option value="all">Every status</option>
          </select>
          <select value={params.get('vendor') || ''} aria-label="Vendor" onChange={(e) => setParam('vendor', e.target.value)}>
            <option value="">Every vendor</option>
            {(data?.vendors || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          {companies.length > 0 && (
            <select value={params.get('company') || ''} aria-label="Company" onChange={(e) => setParam('company', e.target.value)}>
              <option value="">Every company</option>
              <option value="none">The firm (no company)</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select value={params.get('type') || ''} aria-label="Type" onChange={(e) => setParam('type', e.target.value)}>
            <option value="">Every type</option>
            {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={params.get('due') || ''} aria-label="Renewing" onChange={(e) => setParam('due', e.target.value)}>
            <option value="">Any renewal date</option>
            <option value="7">Renewing within 7 days</option>
            <option value="30">Renewing within 30 days</option>
            <option value="90">Renewing within 90 days</option>
            <option value="365">Renewing within a year</option>
          </select>
          <label className="as-check">
            <input type="checkbox" checked={params.get('over') === 'true'} onChange={(e) => setParam('over', e.target.checked ? 'true' : '')} />
            Over-allocated
          </label>
          <select value={params.get('sort') || ''} aria-label="Sort" onChange={(e) => setParam('sort', e.target.value)}>
            <option value="">Soonest renewal first</option>
            <option value="product">By product</option>
            <option value="vendor">By vendor</option>
            <option value="cost">Dearest first</option>
          </select>
          {anyFilter && <button type="button" className="btn btn-plain btn-small" onClick={clear}>Clear filters</button>}
        </div>
      </div>

      {error && <p className="as-error" role="alert">{error}</p>}

      {data && (
        <div className="as-reg-results">
          <span data-testid="licence-count">{`${data.total} licence${data.total === 1 ? '' : 's'}`}</span>
          {data.total > 0 && (
            <span className="lic-results-actions">
              <button type="button" className="btn btn-plain btn-small" disabled={Boolean(exporting)} onClick={() => exportAs('csv')}>
                {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
              </button>
              <button type="button" className="btn btn-plain btn-small" disabled={Boolean(exporting)} onClick={() => exportAs('xlsx')}>
                {exporting === 'xlsx' ? 'Exporting…' : 'Export Excel'}
              </button>
            </span>
          )}
        </div>
      )}

      {!data && !error && <p className="as-quiet" aria-busy="true">Loading the licences…</p>}

      {data && rows.length === 0 ? (
        <div className="empty-state" data-testid="licences-empty">
          <p>{anyFilter ? 'No licences match these filters.' : 'No licences yet. Add the first one - Microsoft 365 is usually it.'}</p>
        </div>
      ) : data && (
        <div className="as-table-wrap">
          <table className="as-table as-reg-table lic-table">
            <thead>
              <tr>
                <th>Licence</th><th>Type</th><th>Seats</th><th>Cost</th><th>Next date</th>
                {companies.length > 0 && <th>Company</th>}
                <th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const seats = seatWords(l);
                const nx = nextWords(l);
                const nc = noticeChip(l);
                return (
                  <tr key={l.id} data-licence={l.id} onClick={() => navigate(`/assets/licences/${l.id}`)}>
                    <td data-label="Licence">
                      <a href={`/assets/licences/${l.id}`} className="lic-product"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/assets/licences/${l.id}`); }}>
                        {l.product}
                      </a>
                      {l.documents_count > 0 && (
                        <span className="lic-clip" data-testid="licence-clip" title={`${l.documents_count} document${l.documents_count === 1 ? '' : 's'}`}
                          aria-label={`${l.documents_count} document${l.documents_count === 1 ? '' : 's'} attached`} role="img">
                          <Paperclip />{l.documents_count > 1 ? l.documents_count : null}
                        </span>
                      )}
                      <span className="lic-flags">
                        {l.status !== 'active' && <span className="as-chip" data-tone={statusTone(l.status)}>{STATUS_LABEL[l.status]}</span>}
                        {l.standard && <span className="as-chip" data-tone="info">Every joiner</span>}
                      </span>
                      <span className="as-sub">{[l.vendor, l.reference].filter(Boolean).join(' · ') || ' '}</span>
                    </td>
                    <td data-label="Type">{TYPE_LABEL[l.licence_type]}</td>
                    <td data-label="Seats">
                      <span className="as-chip" data-tone={seats.tone}>{seats.text}</span>
                      {l.seats !== null && l.seats > 0 && (
                        <span className="lic-bar" data-tone={seats.tone === 'success' ? undefined : seats.tone} aria-hidden="true">
                          <span style={{ width: `${seatShare(l)}%` }} />
                        </span>
                      )}
                    </td>
                    <td data-label="Cost">
                      {costWords(l) || <span className="as-quiet">—</span>}
                      {l.annual_cost > 0 && l.billing_period !== 'annual' && (
                        <span className="as-sub">{`${amount(l.annual_cost, l.currency)} a year`}</span>
                      )}
                    </td>
                    <td data-label="Next date">
                      {nx ? <span className="as-chip" data-tone={nx.tone}>{nx.text}</span> : <span className="as-quiet">—</span>}
                      {l.auto_renew && l.status === 'active' && <span className="as-sub">Renews by itself</span>}
                      {nc && <span className="as-sub" data-testid="licence-notice" data-tone={nc.tone}>{nc.text}</span>}
                    </td>
                    {companies.length > 0 && (
                      <td data-label="Company">
                        <CompanyChip name={l.company_name} color={l.company_color} code={l.company_code} none="—" />
                      </td>
                    )}
                    <td data-label="Owner">{l.owner_name || <span className="as-quiet">Nobody</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <LicenceForm options={{ ...(options || {}), vendors: data?.vendors || [] }} onClose={closeForm}
          onSaved={(saved, failed = []) => {
            setAdding(false);
            navigate(`/assets/licences/${saved.licence.id}`, failed.length ? { state: { problem: failed.join(' ') } } : undefined);
          }} />
      )}
    </div>
  );
}
